import React, { useState } from 'react';
import { ProjectDrawingsProps } from './drawing/types';
import { useDrawingBoard } from './drawing/useDrawingBoard';
import DrawingToolbar from './drawing/DrawingToolbar';
import LayerPanel from './drawing/LayerPanel';
import DrawingCanvas from './drawing/DrawingCanvas';
import DrawingGallery from './drawing/DrawingGallery';

export default function ProjectDrawings({ projectId }: ProjectDrawingsProps) {
  const board = useDrawingBoard(projectId);
  const {
    loading, error, showEditor, setShowEditor, saving, drawings,
    isFullscreen, drawingTitle, setDrawingTitle, layers, resetEditor, saveDrawing,
    lightbox, setLightbox
  } = board;

  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialDistance, setInitialDistance] = useState<number | null>(null);

  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
    const delta = e.deltaY;
    const zoomIn = delta < 0;
    const factor = zoomIn ? 1.1 : 0.9;
    const newScale = Math.min(Math.max(scale * factor, 1), 5);
    setScale(newScale);
    if (newScale === 1) setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      e.preventDefault();
      e.stopPropagation();
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  // Touch handlers for pinch-to-zoom
  const getDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      setInitialDistance(getDistance(e.touches));
    } else if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && initialDistance !== null) {
      const currentDistance = getDistance(e.touches);
      const factor = currentDistance / initialDistance;
      const newScale = Math.min(Math.max(scale * factor, 1), 5);
      setScale(newScale);
      setInitialDistance(currentDistance);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setInitialDistance(null);
    setIsDragging(false);
  };

  // Reset zoom when lightbox changes
  React.useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [lightbox]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-800">Çizimler</h2>
          <p className="text-xs text-stone-400 mt-0.5">{drawings.length} çizim</p>
        </div>
        <button
          onClick={() => setShowEditor(true)}
          disabled={saving}
          className="px-3 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer disabled:opacity-50"
        >
          <i className="ri-pencil-line"></i>
          {saving ? 'Kaydediliyor...' : 'Yeni Çizim'}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* SVG Editor */}
      {showEditor && (
        <div
          className={`${isFullscreen ? 'fixed inset-0 z-50 bg-stone-900 flex flex-col' : 'bg-white rounded-xl border border-stone-200 p-4 space-y-4'}`}
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
          } as React.CSSProperties}
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* Fullscreen Top Bar */}
          {isFullscreen && (
            <div className="flex items-center justify-between px-4 py-2 bg-stone-800 border-b border-stone-700 flex-shrink-0">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={drawingTitle}
                  onChange={(e) => setDrawingTitle(e.target.value)}
                  placeholder="Çizim başlığı..."
                  className="px-3 py-1.5 rounded-md bg-stone-700 border border-stone-600 text-white text-sm placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 w-48"
                />
                <span className="text-xs text-stone-400">
                  {layers.reduce((sum, l) => sum + l.paths.length, 0)} çizim
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => board.setIsFullscreen(false)}
                  className="px-3 py-1.5 text-sm text-stone-300 hover:text-white hover:bg-stone-700 rounded-lg transition-colors cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-fullscreen-exit-line"></i>
                  Küçült
                </button>
                <button
                  onClick={resetEditor}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm text-stone-300 hover:text-white hover:bg-stone-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                >
                  İptal
                </button>
                <button
                  onClick={saveDrawing}
                  disabled={saving || layers.reduce((sum, l) => sum + l.paths.length, 0) === 0}
                  className="px-4 py-1.5 bg-amber-500 text-stone-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-save-line mr-1"></i>
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </div>
          )}

          {/* Inline Title Input (only when not fullscreen) */}
          {!isFullscreen && (
            <input
              type="text"
              value={drawingTitle}
              onChange={(e) => setDrawingTitle(e.target.value)}
              placeholder="Çizim başlığı..."
              className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            />
          )}

          {/* Toolbar */}
          <DrawingToolbar board={board} />

          {/* Canvas + Layers Layout */}
          <div className={`flex gap-4 ${isFullscreen ? 'flex-1 min-h-0 overflow-hidden' : 'flex-col lg:flex-row'}`}>
            {/* SVG Canvas */}
            <DrawingCanvas board={board} />

            {/* Layers Panel */}
            <LayerPanel board={board} />
          </div>

          {/* Inline Actions (only when not fullscreen) */}
          {!isFullscreen && (
            <div className="flex gap-3">
              <button
                onClick={resetEditor}
                disabled={saving}
                className="flex-1 py-2.5 border border-stone-300 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={saveDrawing}
                disabled={saving || layers.reduce((sum, l) => sum + l.paths.length, 0) === 0}
                className="flex-1 py-2.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                <i className="ri-save-line mr-1"></i>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Drawings Grid */}
      <DrawingGallery board={board} />

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
          onClick={() => setLightbox(null)}
        >
          <div 
            className="max-w-4xl max-h-[80vh] px-4 flex items-center justify-center overflow-hidden cursor-move" 
            onClick={(e) => e.stopPropagation()}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={lightbox.svg}
              alt={lightbox.title}
              className="max-w-full max-h-[70vh] object-contain rounded-lg bg-white transition-transform duration-200 ease-out select-none pointer-events-none"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              }}
            />
          </div>
          <div className="absolute bottom-10 left-0 right-0 pointer-events-none">
            {lightbox.title && (
              <p className="text-white text-center text-sm mt-3">{lightbox.title}</p>
            )}
          </div>
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      )}
    </div>
  );
}
